import { useState, useEffect, useMemo } from "react";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import { fullName } from "../../actprep-ui/utils/formatting";

export default function AssignStudentToCoachForm({
  studentProfile,
  assignment = null, // For edit mode
  isOpen,
  onClose,
  onSubmit,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    role_assignment: null,
    start_date: dayjs(),
    end_date: null,
    is_active: true,
  });
  const [dateRange, setDateRange] = useState([dayjs(), null]);
  const [errors, setErrors] = useState({});
  const [selectedRoleAssignment, setSelectedRoleAssignment] = useState(null);

  // Calculate valid date range based on selected role assignment
  const validDateRange = useMemo(() => {
    if (!selectedRoleAssignment) return null;

    const roleStart = dayjs(selectedRoleAssignment.data.start_date);
    const roleEnd = selectedRoleAssignment.data?.end_date
      ? dayjs(selectedRoleAssignment.data.end_date)
      : null;

    return {
      minDate: roleStart,
      maxDate: roleEnd,
      startDate: roleStart,
      endDate: roleEnd,
    };
  }, [selectedRoleAssignment]);

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.role_assignment) {
      newErrors.role_assignment = "Role assignment is required";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (formData.end_date && formData.end_date.isBefore(formData.start_date)) {
      newErrors.end_date = "End date must be after start date";
    }

    // Check if dates fall within role assignment window
    if (selectedRoleAssignment && formData.start_date) {
      const roleStart = dayjs(selectedRoleAssignment.data.start_date);
      const roleEnd = selectedRoleAssignment.data.end_date
        ? dayjs(selectedRoleAssignment.data.end_date)
        : null;

      if (formData.start_date.isBefore(roleStart, "day")) {
        newErrors.start_date = `Start date must be on or after ${roleStart.format(
          "MM/DD/YYYY"
        )}`;
      }

      if (roleEnd && formData.start_date.isAfter(roleEnd, "day")) {
        newErrors.start_date = `Start date must be on or before ${roleEnd.format(
          "MM/DD/YYYY"
        )}`;
      }

      if (formData.end_date) {
        if (roleStart && formData.end_date.isBefore(roleStart, "day")) {
          newErrors.end_date = `End date must be on or after ${roleStart.format(
            "MM/DD/YYYY"
          )}`;
        }

        if (roleEnd && formData.end_date.isAfter(roleEnd, "day")) {
          newErrors.end_date = `End date must be on or before ${roleEnd.format(
            "MM/DD/YYYY"
          )}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const payload = {
        student_id: studentProfile.uuid,
        role_assignment_id: formData.role_assignment.uuid,
        start_date: formData.start_date.format("YYYY-MM-DD"),
        end_date: formData.end_date
          ? formData.end_date.format("YYYY-MM-DD")
          : null,
        is_active: formData.is_active,
      };

      const response = assignment
        ? await api.put(
            apiUrls.coaching.studentToCoachLink(assignment.uuid),
            payload
          )
        : await api.post(apiUrls.coaching.studentToCoachLink(), payload);

      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason || "Failed to assign student to coach"
        );
        return;
      }

      showSuccessSnackbar(
        assignment
          ? "Assignment updated successfully!"
          : "Student successfully assigned to coach!"
      );

      if (onSubmit) {
        onSubmit(response.data);
      }

      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.reason || "Failed to assign student to coach";
      showErrorSnackbar(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleReset = () => {
    setFormData({
      role_assigment: null,
      start_date: dayjs(),
      end_date: null,
      is_active: true,
    });
    setDateRange([dayjs(), null]);
    setSelectedRoleAssignment(null);
    setErrors({});
  };

  const updateFormValue = (field, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleRoleAssignmentChange = (newValue) => {
    setSelectedRoleAssignment(newValue);
    updateFormValue("role_assignment", newValue || null);

    // Reset dates when role assignment changes
    setFormData((prev) => ({
      ...prev,
      start_date: dayjs(),
      end_date: null,
    }));
    setDateRange([dayjs(), null]);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setFormData((prev) => ({
      ...prev,
      start_date: newDateRange[0],
      end_date: newDateRange[1],
    }));
  };

  // Initialize form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (assignment) {
        // Edit mode - pre-populate form
        setFormData({
          role_assignment: assignment.role_assignment,
          start_date: dayjs(assignment.start_date),
          end_date: assignment.end_date ? dayjs(assignment.end_date) : null,
          is_active: assignment.is_active,
        });
        setDateRange([
          dayjs(assignment.start_date),
          assignment.end_date ? dayjs(assignment.end_date) : null,
        ]);
        setSelectedRoleAssignment(assignment.role_assignment);
      } else {
        // Create mode - reset form
        handleReset();
      }
    }
  }, [isOpen, assignment]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      component="form"
      onSubmit={handleSubmit}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {assignment ? "Edit Coach Assignment" : "Assign Student to Coach"}
        </Typography>
        {studentProfile && (
          <Typography variant="body2" color="text.secondary">
            {assignment ? "Editing assignment for" : "Assigning"}:{" "}
            {fullName(studentProfile)}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Role Assignment Selection */}
          <Grid size={12}>
            <ModelAutoComplete
              modelName="CoachRoleAssignment"
              label="Coach Role Assignment"
              value={selectedRoleAssignment}
              setValue={handleRoleAssignmentChange}
              error={!!errors.role_assignment}
              TextFieldProps={{
                error: !!errors.role_assignment,
                helperText: errors.role_assignment,
              }}
              includeFields={["start_date", "end_date"]}
              defaultParams={{
                role__in: ["Assistant Coach", "One on One Coach"],
              }}
            />
          </Grid>

          {/* Date Range */}
          <Grid size={12}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Assignment Date Range
            </Typography>
            {validDateRange && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Valid Range:</strong>{" "}
                  {validDateRange.startDate.format("MM/DD/YYYY")}
                  {validDateRange.endDate
                    ? ` to ${validDateRange.endDate.format("MM/DD/YYYY")}`
                    : " to ongoing"}
                </Typography>
              </Alert>
            )}
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              minDate={validDateRange?.minDate}
              maxDate={validDateRange?.maxDate}
              slotProps={{
                textField: {
                  size: "small",
                  variant: "outlined",
                },
                startTextField: {
                  error: !!errors.start_date,
                  helperText: errors.start_date,
                },
                endTextField: {
                  error: !!errors.end_date,
                  helperText: errors.end_date,
                },
              }}
            />
          </Grid>

          {/* Active Switch */}
          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    updateFormValue("is_active", e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Active Assignment"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Inactive assignments will not be considered for current coaching
              relationships
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <ButtonGroup fullWidth disableElevation>
          <LoadingButton onClick={handleCancel} disabled={isLoading}>
            Cancel
          </LoadingButton>
          <LoadingButton loading={isLoading} disabled={isLoading} type="submit">
            {assignment ? "Update" : "Assign"}
          </LoadingButton>
        </ButtonGroup>
      </DialogActions>
    </Dialog>
  );
}
